import { observable, action, computed, makeObservable, observable as mobxObservable } from 'mobx';

import { getObjects, getObjectData, runQuery } from 'src/api';
import { getReports, getReportData } from 'src/api';
import { waitForCompletion } from 'src/api';
import office from 'src/services/office';

import { FlowstepType, isInsertKind } from 'shared/utils/flowstep';

import * as QA from 'shared/utils/queryAnalysis';
import { substituteQuery } from 'src/services/substitution';
import { ObjectsCache } from 'src/services/objectsCache';
import { memoRequest } from 'shared/utils/memoRequest';
import { runAction } from 'src/services/utils';
import { mergeLists, MergeListsAction } from 'shared/utils/utils';


export default class SalesforceStore {
  @observable reports = [];
  @observable reportsData = new Map();
  reportsMemo = memoRequest(async () => await runAction(async () => await getReports({ recent: true }, this.userStore.token)));

  @observable objects = [];
  @observable objectsData = new Map();
  objectsMemo = memoRequest(async () => await runAction(async () => await getObjects({}, this.userStore.token), this.userStore.token));

  soql = null;
  objectsCache = new ObjectsCache();

  requestStore = null;
  userStore = null;

  constructor() {
    makeObservable(this);
  }

  linkStores(stores) {
    Object.assign(this, stores);
  }

  @action async clean() {
    this.reports = [];
    this.reportsData = new Map();
    this.reportsMemo = memoRequest(async () => await runAction(async () => await getReports({ recent: true }, this.userStore.token)));

    this.objects = [];
    this.objectsData = new Map();
    this.objectsCache = new ObjectsCache();
    this.objectsMemo = memoRequest(async () => await runAction(async () => await getObjects({}, this.userStore.token), this.userStore.token));
  }

  @action async startup() {
  }

  @action async fetchRecentReports() {
    this.reports = await this.reportsMemo((data) => {
      this.reports = data;
    });

    return this.reports;
  }

  @action async fetchReports(data) {
    return await getReports(data, this.userStore.token);
  }

  @action async fetchReportData(data) {
    const { reportId } = data;
    let meta = this.reportsData.get(reportId);
    if (!meta) {
      meta = await getReportData(data, this.userStore.token);
      // console.log(`SalesforceStore::fetchReportData of '${data.reportId}'`, meta);
      this.reportsData.set(reportId, meta);
    }
    return meta;
  }

  @action getReportDataIfCached(data) {
    return this.reportsData.get(data.reportId);
  }

  @action async fetchReportFields(data) {
    return (await this.fetchReportData(data)).fields;
  }

  @action addReports(reports) {
    this.reports = mergeLists(this.reports, reports, MergeListsAction.Merge);
  }

  getReportFieldsIfCached(data) {
    return this.getReportDataIfCached(data)?.fields;
  }

  @action async fetchObjects() {
    this.objects = await this.objectsMemo((data) => {
      this.objects = data;
    });

    return this.objects;
  }

  @action async fetchObjectData(data) {
    const { objectName: objName } = data;
    const objectName = objName.toLowerCase();
    let meta = this.objectsData.get(objectName);
    if (!meta) {
      meta = await getObjectData(data, this.userStore.token);
      // console.log(`SalesforceStore::fetchObjectData of '${data.objectName}'`, meta);
      this.objectsData.set(objectName, meta);
    }
    return meta;
  }

  @action async fetchObjectFields(objectName) {
    const { fields } = await this.fetchObjectData({ objectName });
    return fields;
  }

  @action async runQueries(queries, rowCountLimit = Number.POSITIVE_INFINITY) {
    const runResult = await runQuery({ query: queries, rowCountLimit }, this.userStore.token);
    const { completion } = runResult;
    return completion ? await waitForCompletion(completion) : runResult;
  }

  async substituteQuery(query, queryType, queryWorksheetId) {
    return await Excel.run(async (context) => {
      const limits = office.platformLimits;
      return await substituteQuery(context, query, queryType, queryWorksheetId, limits);
    });
  }

  parseSoqlQuery = async (query, options = QA.ParsingOption.All) => {
    await this.importSoql();

    const queryParser = (query) => this.soql.parseQuery(query);
    const objectLoader = async (objectName) => await this.fetchObjectData({ objectName });

    const result = await QA.parseSoqlQuery(query, { queryParser, objectLoader }, { options });
    // console.log('SalesforceStore::parseQuery: result: ', result);
    return result;
  }

  async parseSoslQuery(query, options = QA.ParsingOption.All) {
    const objectLoader = async (objectName) => await this.fetchObjectData({ objectName });
    const result = await QA.parseSoslQuery(query, { objectLoader }, { options });
    // console.log('SalesforceStore::parseSoslQuery: result: ', result);
    return result;
  }

  async getFlowstepColumns(flowstep) {
    const { type, name } = flowstep;

    try {
      if (isInsertKind(type)) {
        return await this.getInsertColumns(flowstep);
      }

      if (type === FlowstepType.Soql || type === FlowstepType.Sosl) {
        return await this.getQueryColumns(flowstep);
      }

      if (type === FlowstepType.Report) {
        return await this.getReportColumns(flowstep);
      }

      return [];
    }
    catch (err) {
      console.log(`Unable to fetch columns of flowstep '${name}' of type ${type}: `, err.message);
      return [];
    }
  }

  async getQueryColumns(flowstep) {
    const { type, options } = flowstep;
    const { query } = options;
    const opt = QA.ParsingOption.Columns;
    const { columns } = type === FlowstepType.Soql
      ? await this.parseSoqlQuery(query, opt)
      : await this.parseSoslQuery(query, opt);
    return columns.reduce((out, column) => {
      const { type, index, rawField, objectName } = column;
      type === QA.ColumnType.Field && out.push({ columnIndex: index, rawField, objectName });
      return out;
    }, []);
  }

  async getReportColumns(flowstep) {
    const excludedFields = flowstep.options.excludedFields ?? [];
    const fields = await this.fetchReportFields({ reportId: flowstep.options.report_id })
    // console.log('SalesforceStore::getReportColumns: ', fields, excludedFields);
    return fields.reduce((out, rawField) => {
      if (!excludedFields.includes(rawField.key)) {
        // Might be empty for some columns, probably generated ones
        const objectName = rawField.entityColumnName?.split('.')[0] ?? '';
        out.push({ columnIndex: out.length, rawField, objectName })
      }
      return out;
    }, []);
  }

  async getInsertColumns(flowstep) {
    // console.log('SalesforceStore::getInsertColumns: ', flowstep);
    const { sfObjectName, mappedHeaders } = flowstep.options.mapping;

    const columns = [];
    for (let columnIndex = 0; columnIndex < mappedHeaders.length; ++columnIndex) {
      const { sfField, isUsed } = mappedHeaders[columnIndex];
      const { upsertRelationship, name } = sfField;
      if (isUsed) {
        const fieldName = upsertRelationship?.refFieldName ?? name;
        const objectName = upsertRelationship?.refObjectName ?? sfObjectName;
        const fields = await this.fetchObjectFields(objectName);
        const rawField = fields.find(x => x.name === fieldName);
        rawField && columns.push({ columnIndex, rawField, objectName });
      }
    }
    return columns;
  }

  async getAssignedLookupField(lookup) {
    const { objectName, fieldName } = lookup;
    const fields = await this.fetchObjectFields(objectName);
    const rawField = fields.find(x => x.name === fieldName);
    if (rawField) {
      return rawField;
    }
  }


  importSoql = async () => {
    if (!this.soql) {
      const { parseQuery } = await import(/* webpackChunkName: "soql" */'soql-parser-js');
      this.soql = { parseQuery };
    }
    return this.soql;
  }
}